<!--
  Settings → AI.

  La clé Claude (Anthropic) de l'ERP. Une seule clé pour tout le logiciel : elle
  sert à l'agent /agent et à la lecture des contrats d'APV (page /apv-encode,
  bouton « Encode » du rapport Harvesting). Elle vit côté serveur
  (agent_settings.claude_api_key) et n'est jamais renvoyée au navigateur : la page
  dit seulement si elle est configurée, et permet de la remplacer ou de la retirer.

  Mêmes routes que la carte de clé de /agent (admin) : les deux pages règlent la
  même clé. Interface en anglais, comme le reste de l'ERP.
-->
<template>
  <div id="ai-settings-page">
    <vx-card title="Claude API key" class="mb-base">
      <p class="mb-4">
        One key for the whole ERP. It is used to read the purchase contracts of the APVs on the
        <b>APV contracts to encode</b> page (the <b>Encode</b> button of the Harvesting morning report),
        and by the agent on the <b>Agent</b> page. The key stays on the server: it is never shown again
        once saved.
      </p>

      <div class="flex flex-wrap items-center mb-4">
        <feather-icon
          :icon="configured ? 'CheckCircleIcon' : 'AlertCircleIcon'"
          :svgClasses="configured ? 'h-5 w-5 text-success' : 'h-5 w-5 text-warning'" />
        <span class="ml-2">
          <template v-if="loading">Checking…</template>
          <template v-else-if="configured"><b>A key is configured.</b></template>
          <template v-else><b class="text-warning">No key configured:</b> contracts cannot be read.</template>
        </span>
      </div>

      <div class="flex flex-wrap items-end">
        <vs-input
          v-model="keyInput"
          type="password"
          class="mr-4 mb-2"
          style="min-width: 320px"
          label="New key"
          placeholder="sk-ant-…"
          autocomplete="off" />
        <vs-button class="mb-2 mr-2" :disabled="busy || !keyInput.trim()" @click="save">Save the key</vs-button>
        <vs-button
          v-if="configured"
          class="mb-2"
          color="danger"
          type="border"
          :disabled="busy"
          @click="confirmRemove = true">Remove the key</vs-button>
      </div>
      <p v-if="confirmRemove" class="mt-2">
        Remove the key? Contract reading and the agent will stop working until a new key is saved.
        <vs-button size="small" color="danger" class="ml-2" :disabled="busy" @click="remove">Yes, remove</vs-button>
        <vs-button size="small" type="flat" class="ml-1" @click="confirmRemove = false">Cancel</vs-button>
      </p>
    </vx-card>

    <vx-card title="How it is used" class="mb-base">
      <ul class="ai-list">
        <li>Create the key on <b>console.anthropic.com</b> → API Keys, in the account that receives the invoices.</li>
        <li>Contracts are read with <b>Claude Opus 5.5</b>. Reading one contract costs about 0.15 to 0.50 USD.</li>
        <li>Claude only reads the documents. Nothing is written in the ERP before a person confirms the reception.</li>
      </ul>
    </vx-card>
  </div>
</template>

<script>
import axios from '@/axios.js'

export default {
  name: 'AiSettings',
  data () {
    return {
      loading: true,
      configured: false,
      keyInput: '',
      busy: false,
      confirmRemove: false
    }
  },
  methods: {
    notify (title, text, color) {
      this.$vs.notify({
        title,
        text,
        color,
        iconPack: 'feather',
        icon: color === 'danger' ? 'icon-alert-circle' : 'icon-check',
        position: 'bottom-right',
        time: 6000
      })
    },
    errorText (e) {
      return (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || 'Error'
    },
    async load () {
      this.loading = true
      try {
        const { data } = await axios.get('/agent/api/key-status')
        this.configured = !!data.configured
      } catch (e) {
        this.notify('AI', this.errorText(e), 'danger')
      } finally {
        this.loading = false
      }
    },
    async save () {
      if (this.busy) return
      this.busy = true
      try {
        const { data } = await axios.put('/agent/api/key', { api_key: this.keyInput.trim() })
        this.configured = !!data.configured
        this.keyInput = ''
        this.notify('AI', 'The key is saved.', 'success')
      } catch (e) {
        this.notify('AI', this.errorText(e), 'danger')
      } finally {
        this.busy = false
      }
    },
    async remove () {
      if (this.busy) return
      this.busy = true
      try {
        const { data } = await axios.put('/agent/api/key', { api_key: '' })
        this.configured = !!data.configured
        this.confirmRemove = false
        this.notify('AI', 'The key is removed.', 'success')
      } catch (e) {
        this.notify('AI', this.errorText(e), 'danger')
      } finally {
        this.busy = false
      }
    }
  },
  created () {
    if (!localStorage.getItem('accessToken')) {
      this.$router.push({ path: '/pages/login', query: { to: this.$route.fullPath } }).catch(() => {})
      return
    }
    const roles = String(localStorage.getItem('login-roles') || '').toLowerCase()
    if (!roles.includes('admin')) {
      this.notify('Denied', 'This page is for administrators', 'danger')
      this.$router.push({ path: '/dashboard/analytics' })
      return
    }
    this.load()
  }
}
</script>

<style scoped>
.ai-list {
  list-style: disc;
  padding-left: 1.25rem;
}
.ai-list li {
  margin-bottom: 0.4rem;
}
</style>
